import {
    ArmoryItem,
    ArgonautTokens,
    KratosTokens,
    LoadoutStats,
    PowerDie,
    PowerReRollStrategy,
    SimulationConfig,
} from '../types';

export const DEFAULT_NUM_SIMULATED_ROLLS = 10000;
export const DEFAULT_REROLL_THRESHOLD = 0.5;
export const DEFAULT_POWER_REROLL_STRATEGY = PowerReRollStrategy.TABLE_HEURISTIC;

export const ATTACK_DICE_SIDES = 10;

export const MAX_RED_DICE = 6;
export const MAX_BLACK_DICE = 4;
export const MAX_WHITE_DICE = 3;
export const MAX_MORTAL_DICE = 3;

export enum SpoilerTag {
    CYCLE_I = 'Cycle I',
    CYCLE_II = 'Cycle II',
    CYCLE_III = 'Cycle III',
    CYCLE_IV = 'Cycle IV',
    CYCLE_V = 'Cycle V',
    MNESTIS_THEATRE = 'Mnestis Theatre',
    PROMO = 'Promo',
}

export const DEFAULT_VISIBLE_SPOILERS = [SpoilerTag.CYCLE_I];

export enum AppTabs {
    ARGONAUT_LOADOUTS = 1,
    LOADOUT_STATS = 2,
    ARMORY_EDITOR = 4,
}

export enum LocalStorageKeys {
    SCHEMA_VERSION = 'schemaVersion',
    APP_STORAGE_RESET_VERSION = 'appStorageResetVersion',
    ARMORY_OVERRIDES = 'armoryOverrides',
    ARMORY_STORAGE_MODEL = 'armoryStorageModel',
    SAVED_LOADOUTS_STATE = 'savedLoadoutsState',
    APP_UI_STATE = 'appUiState',
    SIMULATOR_STATE = 'simulatorState',
    BATTLEFIELD_PREFERENCES = 'battlefieldPreferences',
    TITANS = 'titans',
    KRATOS_TABLES = 'kratosTables',
    WEAPONS = 'weapons',
    GEAR = 'gear',
    MNEMOS = 'mnemos',
    PRIMORDIAL_STATS = 'primordialStats',
    KRATOS_POOL = 'kratosPool',
    SIMULATION_CONFIG = 'simulationConfig',
    LOADOUTS = 'loadouts',
    LOADOUT_STATS = 'loadoutStats',
    ACTIVE_TAB = 'activeTab',
    SIMULATOR_INPUT_TAB = 'simulatorInputTab',
    VISIBLE_TAGS = 'visibleTags',
    ADVANCED_MODE = 'advancedMode',
    SIMULATION_MODE = 'simulationMode',
    SIMULATOR_GUIDE_EXPANDED = 'simulatorGuideExpanded',
    SIMULATOR_SETTINGS_EXPANDED = 'simulatorSettingsExpanded',
    SPOILER_SETTINGS_EXPANDED = 'spoilerSettingsExpanded',
    FAVORITE_LOADOUTS_EXPANDED = 'favoriteLoadoutsExpanded',
    PRIMORDIAL_USE_CALCULATOR = 'primordialUseCalculator',
    SELECTED_PRIMORDIAL_NAME = 'selectedPrimordialName',
    SELECTED_PRIMORDIAL_LEVEL = 'selectedPrimordialLevel',
    SELECTED_BP_LEVEL = 'selectedBPLevel',
    COMPARE_SELECTED_TOKENS = 'compareSelectedTokens',
}
export const ARMORY_STORAGE_MODEL_VERSION = 'overrides-v1';
export const BUNDLED_ARMORY_SHARE_CONTRACT_VERSION = 'bundled-ids-v1';
export const FORCED_APP_STORAGE_RESET_VERSION = '2.0.0';
export const CURRENT_SCHEMA_VERSION = 5;
export const SCHEMA_CHANGES = [
    'initial schema',
    'Argonaut token gates (Ambrosia, Despair, etc.) were added; older stored armory data may need to be reset or refreshed to calculate token-gated abilities correctly',
    'Bundled ATCC armory snapshot is now included by default, and locally stored ATCC data is refreshed automatically on upgrade',
    'ATCC imported item names no longer include the [ATCC] suffix; imported status is tracked internally instead',
    'Armory storage now persists only custom items, bundled items are read-only, uploads cannot replace bundled names, and saved armory loadouts use stable item references',
    'Version 2.0.0 performs a one-time scoped reset of app-owned localStorage, and app UI state, simulator state, and battlefield preferences now live in grouped documents',
];

export const DEFAULT_LOADOUT_STATS: LoadoutStats = {
    name: '',
    attackDice: 0,
    attackReRolls: 0,
    autoHits: 0,
    autoTokens: { break: 0, black: 0, fire: 0, hope: 0, opening: 0, closing: 0, rouse: 0 },
    maxHits: 0,
    overridePowerDice: false,
    perHitPowerDice: [],
    perHitPowerDiceOverride: [[]],
    powerBonus: 0,
    powerDice: [],
    powerReRolls: 0,
    precisionBonus: 0,
};

export const DEFAULT_SIMULATION_CONFIG: SimulationConfig = {
    powerReRollThreshold: DEFAULT_REROLL_THRESHOLD,
    powerReRollStrategy: DEFAULT_POWER_REROLL_STRATEGY,
    numberOfSimulations: DEFAULT_NUM_SIMULATED_ROLLS,
    skipAttackRoll: false,
    disableAttackReRolls: false,
    disablePowerReRolls: false,
    disablePowerDiceLimits: false,
    autoIncrementRage: false,
};

export const EMPTY_KRATOS_POOL: Required<KratosTokens> = {
    break: 0,
    black: 0,
    fire: 0,
    hope: 0,
    opening: 0,
    closing: 0,
    rouse: 0,
};

export const EMPTY_ARGONAUT_TOKENS: Required<ArgonautTokens> = {
    ambrosia: 0,
    despair: 0,
    bleed: 0,
    midas: 0,
    pain: 0,
};

export const DEFAULT_EDITING_ITEM: ArmoryItem = {
    name: '',
    tags: [],
    stats: [],
};

export const RED_POWER_DIE: PowerDie = {
    faces: [
        { power: 0, potential: 0, dot: 1 },
        { power: 0, potential: 1, dot: 0 },
        { power: 1, potential: 0, dot: 0 },
        { power: 1, potential: 1, dot: 0 },
        { power: 1, potential: 1, dot: 0 },
        { power: 2, potential: 0, dot: 1 },
    ],
};

export const BLACK_POWER_DIE: PowerDie = {
    faces: [
        { power: 0, potential: 1, dot: 1 },
        { power: 1, potential: 1, dot: 0 },
        { power: 1, potential: 1, dot: 0 },
        { power: 2, potential: 0, dot: 1 },
        { power: 2, potential: 1, dot: 0 },
        { power: 2, potential: 2, dot: 1 },
    ],
};

export const WHITE_POWER_DIE: PowerDie = {
    faces: [
        { power: 1, potential: 1, dot: 1 },
        { power: 1, potential: 2, dot: 1 },
        { power: 1, potential: 3, dot: 0 },
        { power: 2, potential: 1, dot: 1 },
        { power: 2, potential: 3, dot: 0 },
        { power: 3, potential: 2, dot: 1 },
    ],
};

export const MORTAL_POWER_DIE: PowerDie = {
    faces: [
        { power: 1, potential: 2, dot: 1 },
        { power: 1, potential: 3, dot: 1 },
        { power: 2, potential: 4, dot: 0 },
        { power: 3, potential: 2, dot: 0 },
        { power: 3, potential: 4, dot: 1 },
        { power: 2, potential: 3, dot: 1 },
    ],
};
